import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IdName } from '../../../../interfaces/pricematrixmodel';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DialogData, GetStreamRecurringLoadsResponse, StreamRecurringLoad } from '../../../../interfaces/streamrecurringLoad';
import { SchedulingSitesResponse } from '../../../../interfaces/siteType';
import { forkJoin } from 'rxjs';
import { SiteService } from '../../../../services/siteservice';
import { FeedstockService } from '../../../../services/feedstockservice';
import { SiteTanksResponse } from '../../../../interfaces/regions';
import { MatCardActions, MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CommonModule } from '@angular/common';
import { SaveStreamRecurringLoadsRequest } from '../../../../interfaces/generatorsmodel';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-addedit-recurringloads',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
    DragDropModule
],
  templateUrl: './addedit-recurringloads.component.html',
  styleUrl: './addedit-recurringloads.component.scss'
})
export class AddeditRecurringloadsComponent implements OnInit {

  private dialogRef = inject(MatDialogRef<AddeditRecurringloadsComponent>);
  private fb = inject(FormBuilder);
  public data: DialogData = inject(MAT_DIALOG_DATA);

  recurrLoadForm!: FormGroup;
  isEditMode: boolean = false;
  recurTypeOptions = signal<IdName[]>([]);
  weekdayOptions = signal<IdName[]>([]);  
  daySequenceOptions = signal<IdName[]>([]);
  disposalSiteOptions = signal<SchedulingSitesResponse[]>([]);
  filteredDisposalSites = signal<SchedulingSitesResponse[]>([]);
  tankOptions = signal<SiteTanksResponse[]>([]);
  canSave: boolean = false;
  get monthDayControl(): FormControl {
    // We use the non-null assertion (!) because we know the control is defined in initForm
    return this.recurrLoadForm.get('monthDay') as FormControl;
  }

  noOfLoadsOptions: IdName[] = [
    { id: 1, name: '1' }, { id: 2, name: '2' }, { id: 3, name: '3' },
  ];
  
  constructor(private fsService: FeedstockService,private _dialog: MatDialog,
    private siteServ: SiteService
  ){

  }

  ngOnInit(): void {
    this.canSave = this.data.canSave;
    this.isEditMode = !!this.data.loadToEdit;
    
    this.initForm();
    this.loadInitialLookups(); 
  }
  
  initForm(): void {
    this.recurrLoadForm = this.fb.group({
      id: [null],
      recur_type: [null, Validators.required],
      weekdaysArray: [null],
      NoOfRecurrings: [1], 
      monthDayChecked: [false],
      monthDay: [{ value: null, disabled: false }, [Validators.min(1), Validators.max(31)]],
      daySequence: [null],
      disposalSiteID: [null, Validators.required],
      tankID: [{ value: null, disabled: true }],
    });
    
    this.recurrLoadForm.get('monthDayChecked')?.valueChanges.subscribe(() => {
        this.onMonthDayChange();
    });
  }

  onRecurTypeChange(): void {
    const recurTypeId = this.recurrLoadForm.get('recur_type')?.value;
    this.enableDisableMonthCheckbox(recurTypeId);
    this.enableDisableDaySequence(recurTypeId);
  }

  enableDisableMonthCheckbox(recurTypeId: number | null): void {
    const monthDayCheckControl = this.recurrLoadForm.get('monthDayChecked');
    if (recurTypeId !== 4) { 
        monthDayCheckControl?.disable({ emitEvent: false });
        if (monthDayCheckControl?.value) {
            monthDayCheckControl?.setValue(false); 
        }
    } else {
        monthDayCheckControl?.enable({ emitEvent: false });
    }
    monthDayCheckControl?.updateValueAndValidity();
  }
  
  loadForm(load: GetStreamRecurringLoadsResponse): void {    
    const parsedWeekdays = load.weekDays
      ? load.weekDays.split(',').map(s => s.trim()).filter(s => s)
      : null;
      
    const disposalSiteName = load.disposalSite;
    let selectedDisposalSiteId: string | null = null;
    if (disposalSiteName) {
      const siteOption = this.disposalSiteOptions().find(
          site => site.sid === disposalSiteName
      );
      if (siteOption) {
          selectedDisposalSiteId = siteOption.sid;
      }
    }
      
    const monthDayActive = (load.monthDay && load.monthDay > 0) || false;

    this.recurrLoadForm.patchValue({
      id: load.id,
      streamID: load.streamID,
      recur_type: load.recur_type,
      weekdaysArray: parsedWeekdays,
      NoOfRecurrings: load.noOfRecurrings,
      monthDayChecked: monthDayActive,
      monthDay: load.monthDay,
      daySequence: load.daySequence,
      disposalSiteID: selectedDisposalSiteId,
      tankID: null,
    });
    // Manually run state updates based on loaded data
    this.updateConditionalFields(monthDayActive, selectedDisposalSiteId);

    if (selectedDisposalSiteId !== null) {
      this.loadSiteTanks(selectedDisposalSiteId, load.tankID);
    }

    this.enableDisableMonthCheckbox(load.recur_type ?? 0);
    this.enableDisableDaySequence(load.recur_type ?? 0); 
  }
  
  loadInitialLookups(): void {
    forkJoin({
        recurTypes: this.fsService.getRecurrType(),
        weekdays: this.fsService.getWeekDays(),
        daySequences: this.fsService.getWeekDaysSeq(),
        sites: this.fsService.getSchedulingSites(6,6), 
    }).subscribe({
        next: (results) => {
          console.log("dropdown results", results);
          this.recurTypeOptions.set(results.recurTypes);
          this.weekdayOptions.set(results.weekdays);
          this.daySequenceOptions.set(results.daySequences);
          this.disposalSiteOptions.set(results.sites);
          this.filteredDisposalSites.set(results.sites);
          if (this.isEditMode && this.data.loadToEdit) {
            this.loadForm(this.data.loadToEdit);
          } else {
              this.enableDisableMonthCheckbox(null); 
              this.enableDisableDaySequence(null); 
              this.updateConditionalFields(false, null); 
          }
        }
    });
  }
  
  updateDaySequenceValidation(recurTypeId: number | null): void {
    const daySequenceControl = this.recurrLoadForm.get('daySequence');
    if (recurTypeId === 1) { // Assuming ID 1 is 'Weekly'
        daySequenceControl?.setValidators(Validators.required);
        daySequenceControl?.enable();
    } else {
        daySequenceControl?.clearValidators();
        daySequenceControl?.disable();
        daySequenceControl?.setValue(null);
    }
    daySequenceControl?.updateValueAndValidity();
  }
  
  isDaySequenceRequired(): boolean {
    return this.recurrLoadForm.get('recur_type')?.value === 1; // Assuming 1 is Weekly
  }

  filterDisposalSites(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredDisposalSites.set([...this.disposalSiteOptions()]);
    } else {
      this.filteredDisposalSites.set(this.disposalSiteOptions().filter(
        m => m.siteName.toLowerCase().includes(searchTerm)
      ));
    }
  }
  
  onMonthDayChange(): void {
    const isChecked = this.recurrLoadForm.get('monthDayChecked')?.value;
    const disposalSiteArray = this.recurrLoadForm.get('disposalSiteArray')?.value;
    const recurTypeId = this.recurrLoadForm.get('recur_type')?.value;
    
    this.updateConditionalFields(isChecked, disposalSiteArray);
    
    this.enableDisableDaySequence(recurTypeId);
  }

  enableDisableDaySequence(recurTypeId: number | null): void {
    const daySequenceControl = this.recurrLoadForm.get('daySequence');
    const isMonthDayActive = this.recurrLoadForm.get('monthDayChecked')?.value === true;
    if (recurTypeId === 4 && !isMonthDayActive) { 
        daySequenceControl?.enable();
        daySequenceControl?.setValidators(Validators.required);
    } else {
        daySequenceControl?.disable();
        daySequenceControl?.clearValidators();
        daySequenceControl?.setValue(null);
    }
    daySequenceControl?.updateValueAndValidity();
  }

  updateConditionalFields(monthDayActive: boolean, disposalSiteId: string | null): void {
    const monthDayControl = this.recurrLoadForm.get('monthDay');
    const weekdaysControl = this.recurrLoadForm.get('weekdaysArray');
    const tankControl = this.recurrLoadForm.get('tankID');
    
    // 1. Month Day vs. Weekdays logic
    if (monthDayActive) {
      // Month Day checked: MonthDay enabled/required, Weekdays disabled/not required
      monthDayControl?.enable();
      monthDayControl?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
      weekdaysControl?.disable();
      weekdaysControl?.setValue(null);
      weekdaysControl?.clearValidators();
    } else {
      // Month Day unchecked: MonthDay disabled/not required, Weekdays enabled/required
      monthDayControl?.disable();
      monthDayControl?.setValue(null);
      monthDayControl?.clearValidators();
      weekdaysControl?.enable();
      weekdaysControl?.setValidators(Validators.required);
    }
    monthDayControl?.updateValueAndValidity();
    weekdaysControl?.updateValueAndValidity();
    
    if (disposalSiteId !== null) { // Check for single value
      tankControl?.enable();
      //tankControl?.setValidators(Validators.required);
    } else {
      tankControl?.disable();
      tankControl?.setValue(null);
      tankControl?.clearValidators();
      this.tankOptions.set([]);
    }
    tankControl?.updateValueAndValidity();
  }
  
  onRecurrLoadSiteChange(): void {
    const selectedSiteId = this.recurrLoadForm.get('disposalSiteID')?.value; 
    if (selectedSiteId !== null) {
        this.loadSiteTanks(selectedSiteId);
    } else {
        this.tankOptions.set([]);
    }
    this.updateConditionalFields(this.recurrLoadForm.get('monthDayChecked')?.value, selectedSiteId);
  }
  
  loadSiteTanks(siteId: string, tankIdToPatch?: number | null): void {
    this.siteServ.getSiteTanks(siteId).subscribe({
      next: (tanks: SiteTanksResponse[]) => {
        console.log("tanks", tanks);
        this.tankOptions.set(tanks);
         const tankControl = this.recurrLoadForm.get('tankID');

        if (tankIdToPatch !== undefined && tankIdToPatch !== null) {
            const isPatchedTankValid = tanks.some(t => t.tankID === tankIdToPatch);
            if (isPatchedTankValid) {
              tankControl?.setValue(tankIdToPatch);
            } else {
              tankControl?.setValue(null);
            }
        } else {
            const currentTankId = tankControl?.value;
            const isTankValid = tanks.some(t => t.tankID === currentTankId);
            if (!isTankValid) {
                 tankControl?.setValue(null);
            }
        }        
        tankControl?.updateValueAndValidity();
      }
    });
  }
  
  saveRecurringLoad(): void {
    this.recurrLoadForm.markAllAsTouched();
    if (!this.recurrLoadForm.valid) {
      console.error('Form is invalid. Errors found:', this.getFormValidationErrors());
      return;
    }

    const formValue = this.recurrLoadForm.getRawValue() as StreamRecurringLoad;
    
    let finalWeekdays: string[] | null = formValue.weekdaysArray || null;
    let finalMonthDay: number | null = formValue.monthDay || null;
    let finalDaySequence: number | null = formValue.daySequence || null;

    if (this.recurrLoadForm.get('monthDayChecked')?.value) {
        finalWeekdays = null;
        finalDaySequence = null; 
    } else {
        finalMonthDay = null;
        if (formValue.recur_type === 1 || formValue.recur_type === 2) {
            finalDaySequence = null;
        }
    }
    
    const payloadWeekDays = (finalWeekdays && finalWeekdays.length > 0) 
        ? finalWeekdays.join(',')
        : '';
    const selectedSiteId = formValue.disposalSiteID;
        
    let payloadDisposalSiteName: string = '';

    if (selectedSiteId !== null) {
        const siteOption = this.disposalSiteOptions().find(site => site.sid === selectedSiteId);
        payloadDisposalSiteName = siteOption ? siteOption.sid : '';
    }
    
    var payload: SaveStreamRecurringLoadsRequest = {
        id: formValue.id || 0,
        recur_type: formValue.recur_type || 0,
        weekDays: payloadWeekDays,
        daySequence: finalDaySequence || 0,
        customerID: this.data.genId,
        streamID: this.data.streamId,
        tankID: formValue.tankID || 0,
        monthDay: finalMonthDay || 0,
        disposalSite: payloadDisposalSiteName,
        noOfRecurrings: formValue.NoOfRecurrings || 0
    };

    console.log('Form recurr data to save:', payload);

    this.fsService.saveRecurringLoad(payload).subscribe({
      next: (response) => {
        console.log('Save successful:', response);
        this.dialogRef.close(true);
      }
    });
  }
  
  getFormValidationErrors() {
    const errors: any = {};
    Object.keys(this.recurrLoadForm.controls).forEach(key => {
      const controlErrors = this.recurrLoadForm.get(key)?.errors;
      if (controlErrors != null) {
          errors[key] = controlErrors;
      }
    });
    return errors;
  }
  
  closeModal(): void {
    this.dialogRef.close(false);
  }
}
